import { Vector3 as BABYLON_Vector3, Quaternion as BABYLON_Quaternion } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Animation as BABYLON_Animation } from '@inter-ikea-betrakta-babylonjs/core/Animations/animation.js';
import { AnimationGroup as BABYLON_AnimationGroup } from '@inter-ikea-betrakta-babylonjs/core/Animations/animationGroup.js';
import { EasingFunction as BABYLON_EasingFunction, CubicEase as BABYLON_CubicEase } from '@inter-ikea-betrakta-babylonjs/core/Animations/easing.js';
import { AnimationProperty, AnimationInterpolationFunction } from '../api.js';
function convert_value(value, property) {
    if (property === AnimationProperty.POSITION || property === AnimationProperty.SCALE) {
        return new BABYLON_Vector3(value.x, value.y, value.z);
    }
    if (property === AnimationProperty.ROTATION) {
        return new BABYLON_Quaternion(value.x, value.y, value.z, value.w);
    }
    throw new Error(`AnimationSystem: Can't convert value ${value} for target propert '${property}'.`);
}
const PROPERTY_TO_DATA_TYPE_TABLE = (() => {
    const table = {};
    table[AnimationProperty.POSITION] = BABYLON_Animation.ANIMATIONTYPE_VECTOR3;
    table[AnimationProperty.ROTATION] = BABYLON_Animation.ANIMATIONTYPE_QUATERNION;
    table[AnimationProperty.SCALE] = BABYLON_Animation.ANIMATIONTYPE_VECTOR3;
    return table;
})();
const INTERPOLATION_MODE_TO_EASING_FUNCTION_TABLE = (() => {
    const table = {};
    table[AnimationInterpolationFunction.LINEAR] = null;
    table[AnimationInterpolationFunction.CUBIC] = new BABYLON_CubicEase();
    table[AnimationInterpolationFunction.CUBIC].setEasingMode(BABYLON_EasingFunction.EASINGMODE_EASEINOUT);
    return table;
})();
export class AnimationSystem {
    constructor(viewer) {
        this._animation_groups = {};
        this._active_animations = 0;
        this._viewer = viewer;
        // TODO: This update observable code is a Babylon bug workaround
        // Bug report: https://forum.babylonjs.com/t/transform-setters-in-transformnode-do-not-dirty-children-different-behaviour-than-markasdirty/54260
        // We have to forcefully and manually dirty stuff from animations.
        // Remove once Babylon has fixed and we've updated.
        this._viewer.get_update_observable().add(() => {
            for (const string_id in this._animation_groups) {
                const id = Number.parseInt(string_id);
                if (!this._viewer.viewer_scene.objects.contains(id))
                    continue;
                const animations = this._animation_groups[string_id];
                const object = this._viewer.viewer_scene.objects.get(id);
                for (let i = 0; i < animations.length; i++) {
                    if (this.animation_running(id, i)) {
                        // Due to the same Babylon bug as mentioned above, we must forcefully set the transform node's _isDirty flag
                        // to false. This is done by calling freezeWorldMatrix (?).
                        object.root_node.freezeWorldMatrix();
                        // Now we can mark the object's transform as dirty.
                        object.mark_transform_dirty();
                        break;
                    }
                }
            }
        });
        // End of Babylon bug workaround.
    }
    register_animation(object, animation_group) {
        const count = (this._animation_groups[object] || []).length;
        if (!this._animation_groups[object]) {
            this._animation_groups[object] = [];
        }
        animation_group.onAnimationGroupEndObservable.add(() => {
            this._active_animations -= 1;
        });
        this._animation_groups[object].push(animation_group);
        return count;
    }
    create_animation(object, animation) {
        const babylon_animations = animation.channels.map((ch, idx) => {
            const anim_ch = new BABYLON_Animation(`${animation.name}_ch_${idx}`, ch.target_property, 30, PROPERTY_TO_DATA_TYPE_TABLE[ch.target_property]);
            anim_ch.setKeys(ch.key_frames.map(key => {
                key.value = convert_value(key.value, ch.target_property);
                return key;
            }));
            anim_ch.setEasingFunction(INTERPOLATION_MODE_TO_EASING_FUNCTION_TABLE[ch.interpolation]);
            return anim_ch;
        });
        const count = (this._animation_groups[object] || []).length;
        const group = new BABYLON_AnimationGroup(animation.name || `object_${object}_anim_${count}`, this._viewer.scene);
        for (let i = 0; i < babylon_animations.length; i++) {
            // Associate animations with their targets.
            const target_id = animation.channels[i].target === undefined ? object : animation.channels[i].target;
            group.addTargetedAnimation(babylon_animations[i], this._viewer.viewer_scene.objects.get(target_id).root);
        }
        return this.register_animation(object, group);
    }
    start_animation(object, animation, from, to) {
        this._animation_groups[object][animation].start(undefined, undefined, from, to);
        this._active_animations += 1;
    }
    stop_animation(object, animation) {
        const frame = this._animation_groups[object][animation].targetedAnimations[0].animation.runtimeAnimations[0].currentFrame;
        this._animation_groups[object][animation].stop();
        return frame;
    }
    animation_running(id, animation_idx) {
        return this._animation_groups[id][animation_idx].isPlaying;
    }
    animations_running() {
        return this._active_animations > 0;
    }
    get_animations(id) {
        return (this._animation_groups[id] || []).map((group, index) => {
            return {
                name: group.name,
                index: index
            };
        });
    }
    delete_animations(object, animations) {
        if (this._animation_groups[object] !== undefined) {
            const object_animations = this._animation_groups[object];
            const new_animations = [];
            for (let i = 0; i < object_animations.length; i++) {
                if (animations.indexOf(i) === -1) {
                    new_animations.push(object_animations[i]);
                }
                else if (this.animation_running(object, i)) {
                    this.stop_animation(object, i);
                }
            }
            this._animation_groups[object] = new_animations;
        }
    }
}
//# sourceMappingURL=AnimationSystem.js.map